import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { NotificationProvider } from "@/context/NotificationContext";
import AdminShell from "@/components/layout/AdminShell";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import LegalPage from "@/pages/LegalPage";
import Dashboard from "@/features/dashboard/DashboardPage";
import Commercial from "@/pages/Commercial";
import Monitoring from "@/pages/Monitoring";
import TenantsPage from "@/pages/Tenants";
import TenantDetail from "@/pages/TenantDetail";
import CompaniesPage from "@/pages/Companies";
import UsersPage from "@/pages/Users";
import RolesPage from "@/pages/Roles";
import PermissionsPage from "@/pages/Permissions";
import PlansPage from "@/pages/Plans";
import PackagesPage from "@/pages/Packages";
import ModulesPage from "@/pages/Modules";
import FeaturesPage from "@/pages/Features";
import EntitlementsPage from "@/pages/Entitlements";
import SubscriptionsPage from "@/pages/Subscriptions";
import UsagePage from "@/pages/Usage";
import IndustryPacksPage from "@/pages/IndustryPacks";
import WhiteLabelPage from "@/pages/WhiteLabel";
import TrialsPage from "@/pages/Trials";
import { PartnersPage, ResellersPage } from "@/pages/Channel";
import SettingsPage from "@/pages/Settings";
import OrganizationSettingsPage from "@/pages/OrganizationSettings";
// ReasonLoyaltyIQ — Loyalty & Rewards Intelligence (pack-gated add-on)
import PackConfiguration from "@/pages/PackConfiguration";
import LoyaltyDashboard from "@/features/loyalty/LoyaltyDashboard";
import LoyaltyProgramsPage from "@/features/loyalty/LoyaltyProgramsPage";
import RewardRulesPage from "@/features/loyalty/RewardRulesPage";
import CustomerWalletPage from "@/features/loyalty/CustomerWalletPage";
import TransactionsPage from "@/features/loyalty/TransactionsPage";
import LoyaltySettingsPage from "@/features/loyalty/LoyaltySettingsPage";
import MembershipPage from "@/features/loyalty/MembershipPage";
import ReferralPage from "@/features/loyalty/ReferralPage";
import CouponsPage from "@/features/loyalty/CouponsPage";
import AiLoyaltyPage from "@/features/loyalty/AiLoyaltyPage";
import LoyaltyAnalyticsPage from "@/features/loyalty/LoyaltyAnalyticsPage";
import OmnichannelPage from "@/features/loyalty/OmnichannelPage";
import AuditPage from "@/pages/Audit";
import SecurityEventsPage from "@/pages/SecurityEvents";
import ComplianceDashboard from "@/features/compliance/ComplianceDashboard";
import ConsentLedgerPage from "@/features/compliance/ConsentLedgerPage";
import PrivacyRequestsPage from "@/features/compliance/PrivacyRequestsPage";
import RetentionPage from "@/features/compliance/RetentionPage";
import DataInventoryPage from "@/features/compliance/DataInventoryPage";
import LanguageGovernance from "@/features/i18n_governance/LanguageGovernance";
import VoiceConfigPage from "@/features/ai/VoiceConfigPage";
import { RequirePermission } from "@/components/common/RequirePermission";
import PublicPrivacyRequest from "@/features/compliance/PublicPrivacyRequest";
import NotificationsPage from "@/features/notifications/NotificationsPage";
import FilesPage from "@/features/files/FilesPage";
import AiFoundationPage from "@/features/ai-foundation/AiFoundationPage";
import CustomersPage from "@/features/customers/CustomersPage";
import CustomerDetail from "@/features/customers/CustomerDetail";
import Customer360Page from "@/features/customers/Customer360Page";
import ContactsPage from "@/features/contacts/ContactsPage";
import ContactDetail from "@/features/contacts/ContactDetail";
import SegmentsPage from "@/features/segments/SegmentsPage";
// Phase 3 — CRM
import CrmDashboardPage from "@/features/crm/CrmDashboardPage";
import LeadsPage from "@/features/crm/LeadsPage";
import LeadDetail from "@/features/crm/LeadDetail";
import LeadSourcesPage from "@/features/crm/LeadSourcesPage";
import OpportunitiesPage from "@/features/crm/OpportunitiesPage";
import OpportunityDetail from "@/features/crm/OpportunityDetail";
import PipelinePage from "@/features/crm/PipelinePage";
import SalesActivitiesPage from "@/features/crm/SalesActivitiesPage";
import TasksPage from "@/features/crm/TasksPage";
import ForecastPage from "@/features/crm/ForecastPage";
// Phase 4 — Omnichannel Inbox
import InboxPage from "@/features/inbox/InboxPage";
import SupervisorPage from "@/features/inbox/SupervisorPage";
import ChannelsPage from "@/features/inbox/ChannelsPage";
import CommTemplatesPage from "@/features/inbox/CommTemplatesPage";
import RoutingRulesPage from "@/features/inbox/RoutingRulesPage";
// Phase 6 — Customer Service & Ticketing
import ServiceDashboard from "@/features/service/ServiceDashboard";
import TicketsPage from "@/features/service/TicketsPage";
import TicketDetail from "@/features/service/TicketDetail";
import CasesPage from "@/features/service/CasesPage";
import SlaPoliciesPage from "@/features/service/SlaPoliciesPage";
import EscalationRulesPage from "@/features/service/EscalationRulesPage";
import FeedbackPage from "@/features/service/FeedbackPage";
import PublicFeedback from "@/features/service/PublicFeedback";
// Phase 7 — Campaign Management
import CampaignDashboard from "@/features/campaigns/CampaignDashboard";
import CampaignsListPage from "@/features/campaigns/CampaignsListPage";
import CampaignDetail from "@/features/campaigns/CampaignDetail";
import UnsubscribePage from "@/features/campaigns/UnsubscribePage";
// Phase 8 — Knowledge Management
import KnowledgeDashboard from "@/features/knowledge/KnowledgeDashboard";
import ArticlesPage from "@/features/knowledge/ArticlesPage";
import ArticleDetail from "@/features/knowledge/ArticleDetail";
import FaqsPage from "@/features/knowledge/FaqsPage";
import DocumentsPage from "@/features/knowledge/DocumentsPage";
import CategoriesPage from "@/features/knowledge/CategoriesPage";
import TagsPage from "@/features/knowledge/TagsPage";
import KnowledgeSearch from "@/features/knowledge/KnowledgeSearch";
// Phase 9 — AI Copilot Foundation
import CopilotPage from "@/features/ai/CopilotPage";
import AiAuditPage from "@/features/ai/AiAuditPage";

import AgentCenterPage from "@/features/ai_agents/AgentCenterPage";
import RecommendationsPage from "@/features/ai_agents/RecommendationsPage";
import ApprovalsPage from "@/features/ai_agents/ApprovalsPage";
import AgentAuditPage from "@/features/ai_agents/AgentAuditPage";
import AgentDetail from "@/features/ai_agents/AgentDetail";

import IntegrationDashboard from "@/features/integrations/IntegrationDashboard";
import ConnectorCatalog from "@/features/integrations/ConnectorCatalog";
import IntegrationsListPage from "@/features/integrations/IntegrationsListPage";
import IntegrationDetail from "@/features/integrations/IntegrationDetail";
import FieldMappingStudio from "@/features/integrations/FieldMappingStudio";
import SyncJobsPage from "@/features/integrations/SyncJobsPage";
import SyncLogsPage from "@/features/integrations/SyncLogsPage";
import ErrorLogsPage from "@/features/integrations/ErrorLogsPage";
import IntegrationMonitoring from "@/features/integrations/IntegrationMonitoring";

import ExecCeoDashboard from "@/features/exec/ExecCeoDashboard";
import SalesLeadershipDashboard from "@/features/exec/SalesLeadershipDashboard";
import MarketingLeadershipDashboard from "@/features/exec/MarketingLeadershipDashboard";
import ServiceLeadershipDashboard from "@/features/exec/ServiceLeadershipDashboard";
import CollectionsPlaceholder from "@/features/exec/CollectionsPlaceholder";
import CustomerRiskRadar from "@/features/exec/CustomerRiskRadar";
import OpportunityRadar from "@/features/exec/OpportunityRadar";
import CampaignPerformanceSummary from "@/features/exec/CampaignPerformanceSummary";
import CustomerHealthOverview from "@/features/exec/CustomerHealthOverview";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <NotificationProvider>
                  <AdminShell />
                </NotificationProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="commercial" element={<Commercial />} />
            <Route path="monitoring" element={<Monitoring />} />
            <Route path="tenants" element={<TenantsPage />} />
            <Route path="tenants/:id" element={<TenantDetail />} />
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="permissions" element={<PermissionsPage />} />
            <Route path="plans" element={<PlansPage />} />
            <Route path="packages" element={<PackagesPage />} />
            <Route path="modules" element={<ModulesPage />} />
            <Route path="features" element={<FeaturesPage />} />
            <Route path="entitlements" element={<EntitlementsPage />} />
            <Route path="subscriptions" element={<SubscriptionsPage />} />
            <Route path="usage" element={<UsagePage />} />
            <Route path="industry-packs" element={<IndustryPacksPage />} />
            <Route path="white-label" element={<WhiteLabelPage />} />
            <Route path="trials" element={<TrialsPage />} />
            <Route path="partners" element={<PartnersPage />} />
            <Route path="resellers" element={<ResellersPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="organization-settings" element={<RequirePermission code="TENANT.CONFIGURE"><OrganizationSettingsPage /></RequirePermission>} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="security/events" element={<RequirePermission code="SECURITY.VIEW"><SecurityEventsPage /></RequirePermission>} />
            {/* Phase 14C — Compliance */}
            <Route path="compliance" element={<RequirePermission code="COMPLIANCE.VIEW"><ComplianceDashboard /></RequirePermission>} />
            <Route path="compliance/consents" element={<RequirePermission code="COMPLIANCE.VIEW"><ConsentLedgerPage /></RequirePermission>} />
            <Route path="compliance/privacy-requests" element={<RequirePermission code="COMPLIANCE.VIEW"><PrivacyRequestsPage /></RequirePermission>} />
            <Route path="compliance/retention" element={<RequirePermission code="COMPLIANCE.VIEW"><RetentionPage /></RequirePermission>} />
            <Route path="compliance/data-inventory" element={<RequirePermission code="COMPLIANCE.VIEW"><DataInventoryPage /></RequirePermission>} />
            {/* Phase 13A Wave 6 — Language Governance Center */}
            <Route path="language-governance" element={<RequirePermission code="LANGUAGE.VIEW"><LanguageGovernance /></RequirePermission>} />
            {/* Phase 13A Wave 5 — Voice AI config */}
            <Route path="voice-config" element={<RequirePermission code="VOICE.CONFIGURE"><VoiceConfigPage /></RequirePermission>} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="files" element={<FilesPage />} />
            <Route path="ai" element={<AiFoundationPage />} />
            <Route path="customer-360" element={<Customer360Page />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="contacts/:id" element={<ContactDetail />} />
            <Route path="segments" element={<SegmentsPage />} />
            {/* Phase 3 — CRM */}
            <Route path="crm" element={<CrmDashboardPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="leads/:id" element={<LeadDetail />} />
            <Route path="lead-sources" element={<LeadSourcesPage />} />
            <Route path="opportunities" element={<OpportunitiesPage />} />
            <Route path="opportunities/:id" element={<OpportunityDetail />} />
            <Route path="pipeline" element={<PipelinePage />} />
            <Route path="sales-activities" element={<SalesActivitiesPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="forecast" element={<ForecastPage />} />
            {/* ReasonLoyaltyIQ — Loyalty & Rewards Intelligence (pack-gated) */}
            <Route path="product/pack-configuration" element={<RequirePermission code="ENTITLEMENT.VIEW"><PackConfiguration /></RequirePermission>} />
            <Route path="loyalty" element={<RequirePermission code="LOYALTY.VIEW"><LoyaltyDashboard /></RequirePermission>} />
            <Route path="loyalty/programs" element={<RequirePermission code="LOYALTY.VIEW"><LoyaltyProgramsPage /></RequirePermission>} />
            <Route path="loyalty/rules" element={<RequirePermission code="LOYALTY.VIEW"><RewardRulesPage /></RequirePermission>} />
            <Route path="loyalty/membership" element={<RequirePermission code="LOYALTY.VIEW"><MembershipPage /></RequirePermission>} />
            <Route path="loyalty/referrals" element={<RequirePermission code="LOYALTY.VIEW"><ReferralPage /></RequirePermission>} />
            <Route path="loyalty/coupons" element={<RequirePermission code="LOYALTY.VIEW"><CouponsPage /></RequirePermission>} />
            <Route path="loyalty/ai" element={<RequirePermission code="LOYALTY.ANALYTICS"><AiLoyaltyPage /></RequirePermission>} />
            <Route path="loyalty/analytics" element={<RequirePermission code="LOYALTY.ANALYTICS"><LoyaltyAnalyticsPage /></RequirePermission>} />
            <Route path="loyalty/omnichannel" element={<RequirePermission code="LOYALTY.VIEW"><OmnichannelPage /></RequirePermission>} />
            <Route path="loyalty/wallets" element={<RequirePermission code="LOYALTY.VIEW"><CustomerWalletPage /></RequirePermission>} />
            <Route path="loyalty/transactions" element={<RequirePermission code="LOYALTY.VIEW"><TransactionsPage /></RequirePermission>} />
            <Route path="loyalty/settings" element={<RequirePermission code="LOYALTY.VIEW"><LoyaltySettingsPage /></RequirePermission>} />
            {/* Phase 4 — Omnichannel Inbox */}
            <Route path="inbox" element={<InboxPage />} />
            <Route path="inbox/supervisor" element={<SupervisorPage />} />
            <Route path="channels" element={<ChannelsPage />} />
            <Route path="comm-templates" element={<CommTemplatesPage />} />
            <Route path="routing-rules" element={<RoutingRulesPage />} />
            {/* Phase 6 — Customer Service & Ticketing */}
            <Route path="service" element={<ServiceDashboard />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="tickets/:id" element={<TicketDetail />} />
            <Route path="cases" element={<CasesPage />} />
            <Route path="sla-policies" element={<SlaPoliciesPage />} />
            <Route path="escalation-rules" element={<EscalationRulesPage />} />
            <Route path="feedback" element={<FeedbackPage />} />
            {/* Phase 7 — Campaign Management */}
            <Route path="campaigns" element={<CampaignDashboard />} />
            <Route path="campaigns/list" element={<CampaignsListPage />} />
            <Route path="campaigns/:id" element={<CampaignDetail />} />
            {/* Phase 8 — Knowledge Management */}
            <Route path="knowledge" element={<KnowledgeDashboard />} />
            <Route path="knowledge/articles" element={<ArticlesPage />} />
            <Route path="knowledge/articles/:id" element={<ArticleDetail />} />
            <Route path="knowledge/faqs" element={<FaqsPage />} />
            <Route path="knowledge/documents" element={<DocumentsPage />} />
            <Route path="knowledge/categories" element={<CategoriesPage />} />
            <Route path="knowledge/tags" element={<TagsPage />} />
            <Route path="knowledge/search" element={<KnowledgeSearch />} />
            {/* Phase 9 — AI Copilot Foundation */}
            <Route path="ai/copilot" element={<CopilotPage />} />
            <Route path="ai/audit" element={<AiAuditPage />} />
            {/* Phase 10 — Agentic AI Agents */}
            <Route path="agents" element={<AgentCenterPage />} />
            <Route path="agents/recommendations" element={<RecommendationsPage />} />
            <Route path="agents/approvals" element={<ApprovalsPage />} />
            <Route path="agents/audit" element={<AgentAuditPage />} />
            <Route path="agents/:code" element={<AgentDetail />} />
            {/* Phase 11 — Enterprise Integration Fabric */}
            <Route path="integrations" element={<IntegrationDashboard />} />
            <Route path="integrations/connectors" element={<ConnectorCatalog />} />
            <Route path="integrations/list" element={<IntegrationsListPage />} />
            <Route path="integrations/sync-jobs" element={<SyncJobsPage />} />
            <Route path="integrations/sync-logs" element={<SyncLogsPage />} />
            <Route path="integrations/errors" element={<ErrorLogsPage />} />
            <Route path="integrations/monitoring" element={<IntegrationMonitoring />} />
            <Route path="integrations/:id/mapping/:target" element={<FieldMappingStudio />} />
            <Route path="integrations/:id" element={<IntegrationDetail />} />
            {/* Phase 12 — Executive Command Center */}
            <Route path="exec/ceo" element={<ExecCeoDashboard />} />
            <Route path="exec/sales" element={<SalesLeadershipDashboard />} />
            <Route path="exec/marketing" element={<MarketingLeadershipDashboard />} />
            <Route path="exec/service" element={<ServiceLeadershipDashboard />} />
            <Route path="exec/collections" element={<CollectionsPlaceholder />} />
            <Route path="exec/risk-radar" element={<CustomerRiskRadar />} />
            <Route path="exec/opportunity-radar" element={<OpportunityRadar />} />
            <Route path="exec/campaign-summary" element={<CampaignPerformanceSummary />} />
            <Route path="exec/customer-health" element={<CustomerHealthOverview />} />
          </Route>
          <Route path="/feedback/:token" element={<PublicFeedback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy-policy" element={<LegalPage />} />
          <Route path="/terms-of-service" element={<LegalPage />} />
          <Route path="/privacy/:tenantId" element={<PublicPrivacyRequest />} />
          <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
          <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
        </Routes>
        </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}

export default App;
